import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { Button, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, TextField } from "@material-ui/core";
import Close from "@material-ui/icons/Close";
import styled from "styled-components";
import { MyBookingWrapper } from "../MyBooking/styled/DesStyled";
import { OrderInner } from "../Order/styled/OrderStyle";
import ProtectedRoute from "../../controls/auth/ProtectedRoute";
import { UserRole } from "../../../constants/roles";
import {
	NotificationBreadcrumb,
	BreadcrumbItem,
	BreadcrumbSeparator,
	NotificationContainer,
	NotificationHeader,
	NotificationTitle,
	NotificationSubtitle,
} from "./styled/NotificationManagementStyles";
import StationPortalBroadcastForm from "./StationPortalBroadcastForm";
import BroadcastPreview from "./BroadcastPreview";
import PermissionGate from "../../controls/auth/PermissionGate";
import {
	getSystemConfig,
	saveSystemConfig,
	publishBroadcastDraft,
	unpublishBroadcast,
	deleteBroadcastHistory,
	StationPortalBroadcastHistoryItem,
} from "../../../services/SystemConfigService";
import sanitizeHtml from "../../../utils/sanitizeHtml";

/** Trùng `STATION_BROADCAST_HISTORY_DB_EMPTY_SNAPSHOT_ID` trên backend: bản tổng hợp khi DB chưa có cột lịch sử. */
const BROADCAST_LIVE_SNAPSHOT_ID = "station-portal-broadcast-snapshot";

const HeroCard = styled.div`
	background: linear-gradient(135deg, #f0f7ff 0%, #ffffff 50%, #f8fafc 100%);
	border: 1px solid #e2e8f0;
	border-radius: 16px;
	padding: 28px 32px 32px;
	margin-bottom: 28px;
	box-shadow: 0 4px 24px rgba(15, 23, 42, 0.06);
`;

const HeroKicker = styled.p`
	margin: 0 0 8px;
	font-size: 12px;
	font-weight: 600;
	letter-spacing: 0.06em;
	text-transform: uppercase;
	color: #3b82f6;
`;

const HeroTitle = styled.h2`
	margin: 0 0 10px;
	font-size: 22px;
	font-weight: 800;
	color: #0f172a;
	line-height: 1.3;
	@media (max-width: 600px) {
		font-size: 19px;
	}
`;

const HeroText = styled.p`
	margin: 0 0 24px;
	font-size: 14px;
	color: #64748b;
	line-height: 1.65;
	max-width: 52rem;
`;

const PrimaryTrigger = styled(Button)`
	&&.MuiButton-root {
		border-radius: 12px;
		padding: 12px 24px;
		font-weight: 700;
		font-size: 15px;
		text-transform: none;
		box-shadow: 0 4px 14px rgba(37, 99, 235, 0.35);
	}
`;

const HistorySection = styled.div`
	margin-top: 8px;
`;

const HistoryHead = styled.h3`
	margin: 0 0 16px;
	font-size: 16px;
	font-weight: 700;
	color: #0f172a;
`;

const HistoryList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
`;

const HistoryItem = styled.article`
	background: #ffffff;
	border: 1px solid #e5e7eb;
	border-radius: 12px;
	padding: 16px 18px 18px;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
	border-left: 4px solid #2563eb;
`;

const HistoryMeta = styled.div`
	font-size: 12px;
	color: #64748b;
	margin-bottom: 6px;
	display: flex;
	flex-wrap: wrap;
	gap: 8px 16px;
`;

const HistoryTitle = styled.div`
	font-size: 15px;
	font-weight: 700;
	color: #111827;
	margin-bottom: 6px;
`;

const HistoryBody = styled.div`
	font-size: 14px;
	color: #374151;
	white-space: normal;
	word-break: break-word;
	line-height: 1.55;
	margin-bottom: 0;
	max-height: 200px;
	overflow: auto;

	p {
		margin: 0 0 0.5em;
	}
	p:last-child {
		margin-bottom: 0;
	}
`;

const EmptyState = styled.div`
	background: #f9fafb;
	border: 1px dashed #d1d5db;
	border-radius: 12px;
	padding: 24px 20px;
	color: #6b7280;
	font-size: 14px;
	text-align: center;
	line-height: 1.5;
`;

const StatusBadgeDraft = styled.span`
	background: #fef3c7;
	color: #92400e;
	border-radius: 6px;
	padding: 2px 8px;
	font-size: 12px;
	font-weight: 600;
	margin-left: 8px;
	vertical-align: middle;
`;

const StatusBadgePublished = styled.span`
	background: #d1fae5;
	color: #065f46;
	border-radius: 6px;
	padding: 2px 8px;
	font-size: 12px;
	font-weight: 600;
	margin-left: 8px;
	vertical-align: middle;
`;

const HistoryActions = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	margin-top: 10px;
	flex-wrap: wrap;
`;

const displayModeLabel = (d: string) => (d === "floating_button" ? "Nút thông báo nổi" : "Popup (một lần mỗi phiên)");

export default function NotificationManagement() {
	return (
		<ProtectedRoute
			allowedRoles={[UserRole.Admin]}
			fallbackTitle="Access Denied"
			fallbackMessage="This page is only accessible to administrators.">
			<NotificationManagementContent />
		</ProtectedRoute>
	);
}

function NotificationManagementContent() {
	const [history, setHistory] = useState<StationPortalBroadcastHistoryItem[]>([]);
	const [historyLoading, setHistoryLoading] = useState(true);
	const [historyError, setHistoryError] = useState("");
	const [dialogOpen, setDialogOpen] = useState(false);
	const [publishing, setPublishing] = useState<string | null>(null);
	const [unpublishing, setUnpublishing] = useState<string | null>(null);
	const [deleting, setDeleting] = useState<string | null>(null);
	const [previewItem, setPreviewItem] = useState<StationPortalBroadcastHistoryItem | null>(null);
	const [onboardingFromDate, setOnboardingFromDate] = useState("");
	const [onboardingDateSaving, setOnboardingDateSaving] = useState(false);

	const loadHistory = useCallback(async () => {
		setHistoryLoading(true);
		setHistoryError("");
		try {
			const cfg = await getSystemConfig();
			const list = cfg.stationPortalBroadcastHistory;
			setHistory(Array.isArray(list) ? list : []);
			setOnboardingFromDate(String(cfg.stationPortalOnboardingRequiredFromDate || "").slice(0, 10));
		} catch {
			setHistoryError("Không tải được lịch sử thông báo.");
		} finally {
			setHistoryLoading(false);
		}
	}, []);

	const saveOnboardingDate = useCallback(async () => {
		setOnboardingDateSaving(true);
		try {
			const cfg = await getSystemConfig();
			await saveSystemConfig({ ...cfg, stationPortalOnboardingRequiredFromDate: onboardingFromDate });
			window.alert("Đã lưu.");
		} catch (err: any) {
			window.alert(err?.response?.data?.message || "Lưu thất bại.");
		} finally {
			setOnboardingDateSaving(false);
		}
	}, [onboardingFromDate]);

	useEffect(() => {
		void loadHistory();
	}, [loadHistory]);

	const onBroadcastSent = useCallback(() => {
		setDialogOpen(false);
		void loadHistory();
	}, [loadHistory]);

	const onPublish = async (historyId: string) => {
		if (
			!window.confirm(
				"Publish this notification to all stations? Any currently published notification will be unpublished."
			)
		)
			return;
		setPublishing(historyId);
		try {
			await publishBroadcastDraft(historyId);
			window.alert("Published successfully. Stations will see this notification on the portal.");
			void loadHistory();
		} catch (err: any) {
			window.alert(err?.response?.data?.message || "Publish thất bại.");
		} finally {
			setPublishing(null);
		}
	};

	const onUnpublish = async (historyId: string) => {
		if (!window.confirm("Unpublish this notification? Stations will no longer see it.")) return;
		setUnpublishing(historyId);
		try {
			await unpublishBroadcast(historyId);
			void loadHistory();
		} catch (err: any) {
			window.alert(err?.response?.data?.message || "Unpublish failed.");
		} finally {
			setUnpublishing(null);
		}
	};

	const onDelete = async (historyId: string) => {
		if (!window.confirm("Delete this notification? This cannot be undone.")) return;
		setDeleting(historyId);
		try {
			await deleteBroadcastHistory(historyId);
			void loadHistory();
		} catch (err: any) {
			window.alert(err?.response?.data?.message || "Delete failed.");
		} finally {
			setDeleting(null);
		}
	};

	return (
		<MyBookingWrapper>
			<OrderInner>
				<NotificationBreadcrumb>
					<BreadcrumbItem>
						<a
							href="/"
							style={{ textDecoration: "none", color: "inherit" }}>
							Home
						</a>
					</BreadcrumbItem>
					<BreadcrumbSeparator
						src="./static/img/direction-right.png"
						alt=">"
					/>
					<BreadcrumbItem active>Quản lý thông báo</BreadcrumbItem>
				</NotificationBreadcrumb>
			</OrderInner>

			<NotificationContainer>
				<NotificationHeader>
					<div>
						<NotificationTitle>Notifications</NotificationTitle>
						<NotificationSubtitle>
							Trang này tập trung: soạn thông báo tới tất cả trạm (Station portal) và lịch sử đã tạo. Các
							mục tự động booking / thanh toán đã tắt khỏi giao diện ở đây.
						</NotificationSubtitle>
					</div>
				</NotificationHeader>

				<div
					style={{
						background: "#fff",
						border: "1px solid #e5e7eb",
						borderRadius: 12,
						padding: "18px 22px",
						marginBottom: 20,
					}}>
					<div style={{ fontWeight: 700, fontSize: 15, color: "#0f172a", marginBottom: 4 }}>
						Welcome Aboard onboarding
					</div>
					<div style={{ fontSize: 13, color: "#64748b", marginBottom: 12, lineHeight: 1.5 }}>
						Station tạo từ ngày này trở đi mới thấy form Welcome Aboard. Để trống = tắt với tất cả trạm.
					</div>
					<div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
						<TextField
							type="date"
							variant="outlined"
							size="small"
							value={onboardingFromDate}
							onChange={(e) => setOnboardingFromDate(e.target.value || "")}
							disabled={onboardingDateSaving}
							InputLabelProps={{ shrink: true }}
						/>
						<PermissionGate permission="settings.manage">
							<Button
								variant="contained"
								color="primary"
								size="small"
								disabled={onboardingDateSaving}
								onClick={() => void saveOnboardingDate()}
								style={{ textTransform: "none", borderRadius: 8, fontWeight: 600 }}>
								{onboardingDateSaving ? "Đang lưu…" : "Lưu"}
							</Button>
						</PermissionGate>
					</div>
				</div>

				<HeroCard>
					<HeroKicker>Station portal</HeroKicker>
					<HeroTitle>Thông báo tới tất cả trạm</HeroTitle>
					<HeroText>
						Bấm để mở cửa sổ soạn nội dung, chọn cách hiển thị và thời gian — giống luồng cũ, chỉ không còn
						form chiếm cả màn. Lịch sử gửi hiển thị bên dưới sau mỗi lần lưu thành công.
					</HeroText>
					<PermissionGate permission="settings.manage">
						<PrimaryTrigger
							variant="contained"
							color="primary"
							onClick={() => setDialogOpen(true)}>
							Soạn thông báo gửi tới tất cả trạm
						</PrimaryTrigger>
					</PermissionGate>
				</HeroCard>

				<HistorySection>
					<HistoryHead>Thông báo đã tạo / gần đây</HistoryHead>
					{historyError ? (
						<EmptyState style={{ color: "#991B1B", background: "#FEF2F2", borderStyle: "solid" }}>
							{historyError}
						</EmptyState>
					) : null}
					{!historyError && historyLoading ? <EmptyState>Đang tải lịch sử…</EmptyState> : null}
					{!historyError && !historyLoading && history.length === 0 ? (
						<EmptyState>
							Chưa có bản ghi. Khi bạn lưu thành công từ nút ở trên, từng bản sẽ hiện tại đây (kèm nội
							dung và khung thời gian hợp lệ lúc gửi).
						</EmptyState>
					) : null}
					{!historyError && !historyLoading && history.length > 0 ? (
						<HistoryList>
							{history.map((h) => (
								<HistoryItem
									key={h.id}
									style={{ display: "flex", alignItems: "flex-start", gap: "16px" }}>
									<div style={{ flex: 1, minWidth: 0 }}>
										{h.id === BROADCAST_LIVE_SNAPSHOT_ID ? (
											<div
												style={{
													fontSize: 12,
													color: "#4b5563",
													marginBottom: 10,
													lineHeight: 1.45,
													background: "#f0f9ff",
													borderRadius: 8,
													padding: "8px 10px",
												}}>
												Bản từ cấu hình broadcast đang bật. Bấm &quot;Tạo và gửi tới trạm&quot;
												(kể cả giữ nguyên nội dung) để hệ thống lưu bản ghi lịch sử.
											</div>
										) : null}
										<HistoryMeta>
											<span>
												<strong>Gửi/ lưu:</strong> {moment(h.sentAt).format("DD/MM/YYYY HH:mm")}
											</span>
											<span>
												<strong>Hiện:</strong> {displayModeLabel(h.displayMode)}
											</span>
											<span>
												<strong>Kết thúc:</strong>{" "}
												{h.stationPortalBroadcastEndsAt
													? moment(h.stationPortalBroadcastEndsAt).format("DD/MM/YYYY HH:mm")
													: "—"}
											</span>
										</HistoryMeta>
										<HistoryTitle>
											{h.title && h.title.trim()
												? h.title
												: "Thông báo từ WhaleLO (mặc định nếu để trống tiêu đề)"}
											{h.status === "draft" ? (
												<StatusBadgeDraft>Nháp</StatusBadgeDraft>
											) : (
												<StatusBadgePublished>Đã gửi</StatusBadgePublished>
											)}
										</HistoryTitle>
										<HistoryBody
											{...(h.body && h.body.trim()
												? {
														dangerouslySetInnerHTML: {
															__html: sanitizeHtml(h.body),
														},
												  }
												: { children: "—" })}
										/>
										<HistoryActions>
											<Button
												variant="outlined"
												size="small"
												onClick={() => setPreviewItem(h)}
												style={{ textTransform: "none", borderRadius: 8 }}>
												Xem trước
											</Button>
										</HistoryActions>
									</div>
									{h.id !== BROADCAST_LIVE_SNAPSHOT_ID ? (
										<PermissionGate permission="settings.manage">
											<div
												style={{
													display: "flex",
													flexDirection: "column",
													gap: "8px",
													flexShrink: 0,
													minWidth: 120,
													paddingTop: 4,
												}}>
												<Button
													variant={h.status === "published" ? "contained" : "outlined"}
													size="small"
													disabled={publishing === h.id || h.status === "published"}
													onClick={() => void onPublish(h.id)}
													style={
														h.status === "published"
															? {
																	textTransform: "none",
																	borderRadius: 8,
																	backgroundColor: "#059669",
																	color: "#fff",
															  }
															: {
																	textTransform: "none",
																	borderRadius: 8,
																	borderColor: "#059669",
																	color: "#059669",
															  }
													}>
													{publishing === h.id ? (
														<>
															<CircularProgress
																size={14}
																style={{ marginRight: 6 }}
															/>
															Publishing...
														</>
													) : h.status === "published" ? (
														"Published"
													) : (
														"Publish"
													)}
												</Button>
												<Button
													variant="outlined"
													size="small"
													disabled={unpublishing === h.id || h.status !== "published"}
													onClick={() => void onUnpublish(h.id)}
													style={{
														textTransform: "none",
														borderRadius: 8,
														borderColor: "#6B7280",
														color: "#374151",
													}}>
													{unpublishing === h.id ? (
														<>
															<CircularProgress
																size={14}
																style={{ marginRight: 6 }}
															/>
															Unpublishing...
														</>
													) : (
														"Unpublish"
													)}
												</Button>
												<Button
													variant="outlined"
													size="small"
													disabled={deleting === h.id}
													onClick={() => void onDelete(h.id)}
													style={{
														textTransform: "none",
														borderRadius: 8,
														borderColor: "#EF4444",
														color: "#EF4444",
													}}>
													{deleting === h.id ? (
														<>
															<CircularProgress
																size={14}
																style={{ marginRight: 6 }}
															/>
															Deleting...
														</>
													) : (
														"Delete"
													)}
												</Button>
											</div>
										</PermissionGate>
									) : null}
								</HistoryItem>
							))}
						</HistoryList>
					) : null}
				</HistorySection>
			</NotificationContainer>

			<Dialog
				open={dialogOpen}
				onClose={() => setDialogOpen(false)}
				maxWidth="md"
				fullWidth
				scroll="paper"
				disableEnforceFocus
				disableAutoFocus
				PaperProps={{
					style: {
						borderRadius: 16,
						maxHeight: "calc(100% - 64px)",
					},
				}}>
				<DialogTitle
					style={{
						position: "relative",
						background: "linear-gradient(135deg, #1d4ed8 0%, #2563eb 50%, #3b82f6 100%)",
						color: "#fff",
						padding: "20px 56px 20px 24px",
						margin: 0,
					}}>
					<div
						style={{
							fontSize: 11,
							fontWeight: 600,
							letterSpacing: "0.05em",
							textTransform: "uppercase",
							opacity: 0.92,
							marginBottom: 4,
						}}>
						Station portal
					</div>
					<div style={{ fontSize: 20, fontWeight: 800, lineHeight: 1.3 }}>Thông báo tới tất cả trạm</div>
					<IconButton
						aria-label="close"
						style={{ position: "absolute", right: 8, top: 8, color: "#fff" }}
						onClick={() => setDialogOpen(false)}>
						<Close />
					</IconButton>
				</DialogTitle>
				<DialogContent
					style={{
						padding: "16px 24px 28px",
						background: "linear-gradient(180deg, #f8fafc 0%, #fff 12%)",
					}}>
					{dialogOpen && (
						<StationPortalBroadcastForm
							layout="embed"
							onSent={onBroadcastSent}
							onDraftSaved={onBroadcastSent}
						/>
					)}
				</DialogContent>
			</Dialog>

			{previewItem && (
				<BroadcastPreview
					open={!!previewItem}
					onClose={() => setPreviewItem(null)}
					title={previewItem.title}
					bodyHtml={previewItem.body}
					displayMode={previewItem.displayMode}
				/>
			)}
		</MyBookingWrapper>
	);
}
